"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { TypingText } from "../../../components/ui/TypingText";
import { ThreeDMarquee } from "../../../components/ui/ThreeDMarquee"; // ✅ Import

const HeroSection = () => {
  const [currentMetric, setCurrentMetric] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const metrics = [
    { label: "Students Trained", value: "2500+", icon: "User" },
    { label: "Internship Applied", value: "200+", icon: "TrendingUp" },
    { label: "Course Enrolled", value: "100+", icon: "Book" },
  ];

  const projectImages = [
    "/assets/images/ct1.jpg?w=400&h=300&fit=crop",
    "/assets/images/gr1.jpg?w=400&h=300&fit=crop",
    "/assets/images/gr2.jpg?w=400&h=300&fit=crop",
    "/assets/images/ct4.jpg?w=400&h=300&fit=crop",
    "/assets/images/gr3.jpg?w=400&h=300&fit=crop",
    "/assets/images/gr4.jpg?w=400&h=300&fit=crop",
    "/assets/images/ct2.jpg?w=400&h=300&fit=crop",
    "/assets/images/ct3.jpg?w=400&h=300&fit=crop",
    "/assets/images/ct4.jpg?w=400&h=300&fit=crop",
    "/assets/images/ct5.jpg?w=400&h=300&fit=crop",
    "/assets/images/ct6.jpg?w=400&h=300&fit=crop",
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % metrics.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen bg-gradient-to-br from-background via-surface to-muted overflow-hidden"
    >
      {/* Background Animation */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-secondary rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-accent rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-5rem)]">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -50 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Animated Main Headline */}
            <div className="leading-tight space-y-1">
              {/* Line 1 */}
              <TypingText
                  as="h1"
                  fontSize="text-5xl"
                  fontWeight="font-bold"
                  className="sm:whitespace-nowrap whitespace-normal tracking-tight"
                >
                  <span>
                  <span className="text-nipix-blue">N</span>
                  <span className="text-text-primary">ext </span>
                  <span className="text-nipix-blue">I</span>
                  <span className="text-text-primary">nnovation</span>
                </span>
              </TypingText>

              {/* Line 2 */}
              <TypingText
                  as="h1"
                  fontSize="text-5xl"
                  fontWeight="font-bold"
                  className="sm:whitespace-nowrap whitespace-normal tracking-tight"
                >
                  <span>
                  <span className="text-nipix-blue">P</span>
                  <span className="text-text-primary">latform for</span>
                </span>
              </TypingText>

              {/* Line 3 */}
              <TypingText
                  as="h1"
                  fontSize="text-5xl"
                  fontWeight="font-bold"
                  className="sm:whitespace-nowrap whitespace-normal tracking-tight"
              >
                  <span>
                  <span className="text-nipix-blue">I</span>
                  <span className="text-text-primary">nspired </span>
                  <span className="text-nipix-blue">X</span>
                  <span className="text-text-primary">perience</span>
                </span>
              </TypingText>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg sm:text-xl text-text-secondary leading-relaxed max-w-2xl"
              >
                Get hands-on experience through certificate internships and
                industry-focused courses that employers actually want. Transform
                from theory to practice in India's competitive tech market.
              </motion.p>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                variant="default"
                size="lg"
                onClick={() => scrollToSection("apply-section")}
                iconName="Briefcase"
                iconPosition="left"
                className="btn-primary text-lg px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Apply for Internship
                <span className="ml-2 text-sm opacity-90">
                </span>
              </Button>
            </motion.div>

            {/* Trust Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-border"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                    <Icon
                      name={metrics[currentMetric].icon}
                      size={24}
                      color="white"
                      strokeWidth={2}
                    />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">
                      {metrics[currentMetric].value}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {metrics[currentMetric].label}
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2">
                  {metrics.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                        index === currentMetric ? "bg-primary" : "bg-border"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side → Clean 3D Marquee */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : 50 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <ThreeDMarquee
              images={projectImages.map((src, idx) => ({
                src,
                alt: `Student Project ${idx + 1}`,
              }))}
              cols={2} // ✅ Adjust columns (try 3 or 4)
            />
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        transition={{ duration: 0.8, delay: 1.5 }}
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center space-y-6 text-text-secondary">
          <span className="text-sm">Scroll to explore</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Icon name="ChevronDown" size={20} strokeWidth={2} />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
